/**
 * SpectatorModeUI - The spectator mode display for sitting-out players.
 *
 * When a player sits out during an odd-player round, this component:
 * - Displays that the player is spectating (not participating in combat)
 * - Shows which combat they are watching (player A vs player B)
 * - Notifies them of the 3-gold sit-out reward
 * - Ensures no player-action controls are shown during spectating
 * - Reacts to SpectatorCombatSelected and SitOutNotification messages
 * - Integrates with the combat board view to display the spectated combat
 *
 * The backend is authoritative; this component only renders spectator state
 * that the server sends via SpectatorCombatSelected, SitOutNotification,
 * CombatStarted, and CombatResult messages.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import {
  SpectatorCombatSelected,
  SitOutNotification,
  CombatStarted,
  CombatResult,
  PROTOCOL_VERSION,
} from '../../shared/messages/MessageContracts';

// ─── Spectator Status ───────────────────────────────────────────────────────

/**
 * The current spectator status for the player.
 */
export type SpectatorStatus = 'idle' | 'waiting' | 'watching' | 'finished';

// ─── Spectator Mode Render Data ─────────────────────────────────────────────

/**
 * Render data for the spectator mode UI.
 */
export interface SpectatorModeRenderData {
  /** Whether the spectator mode UI should be visible. */
  isVisible: boolean;

  /** Current spectator status. */
  status: SpectatorStatus;

  /** Whether the current player is spectating this round. */
  isSpectating: boolean;

  /** Player A of the combat being watched (null if not yet selected). */
  watchedPlayerAId: string | null;

  /** Player B of the combat being watched (null if not yet selected). */
  watchedPlayerBId: string | null;

  /** Whether the watched combat has started. */
  combatStarted: boolean;

  /** Whether the watched combat has ended. */
  combatEnded: boolean;

  /** Gold awarded for sitting out (3 by default). */
  sitOutGoldAwarded: number;

  /** Whether the sit-out gold has been confirmed via SitOutNotification. */
  sitOutGoldConfirmed: boolean;

  /** Whether player-action controls should be hidden (true while spectating). */
  hidePlayerControls: boolean;

  /** Human-readable message describing the spectator state. */
  spectatorMessage: string;

  /** The raw SpectatorCombatSelected message (null if not yet received). */
  spectatorSelection: SpectatorCombatSelected | null;

  /** The raw SitOutNotification message (null if not yet received). */
  sitOutNotification: SitOutNotification | null;

  /** The raw CombatStarted message for the watched combat. */
  watchedCombatStarted: CombatStarted | null;

  /** The raw CombatResult for the watched combat (null if not yet received). */
  watchedCombatResult: CombatResult | null;

  /** Match ID. */
  matchId: string | null;
}

/**
 * Callback invoked when render data changes.
 */
export type SpectatorModeRenderCallback = (data: SpectatorModeRenderData) => void;

/**
 * Callback invoked when the spectator selection is received.
 */
export type SpectatorSelectedCallback = (selection: SpectatorCombatSelected) => void;

/**
 * Callback invoked when the sit-out gold notification is received.
 */
export type SitOutGoldCallback = (notification: SitOutNotification) => void;

// ─── Internal State ─────────────────────────────────────────────────────────

interface InternalSpectatorState {
  spectatorSelection: SpectatorCombatSelected | null;
  sitOutNotification: SitOutNotification | null;
  watchedCombatStarted: CombatStarted | null;
  watchedCombatResult: CombatResult | null;
}

// ─── Spectator Mode UI ──────────────────────────────────────────────────────

export class SpectatorModeUI {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: SpectatorModeRenderCallback[] = [];
  private _selectedCallbacks: SpectatorSelectedCallback[] = [];
  private _goldCallbacks: SitOutGoldCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  // Internal spectator state
  private _state: InternalSpectatorState = {
    spectatorSelection: null,
    sitOutNotification: null,
    watchedCombatStarted: null,
    watchedCombatResult: null,
  };
  private _manuallyReset: boolean = false;
  private _previousPhase: string = '';

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): SpectatorModeRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the spectator mode UI is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns the current spectator status. */
  get status(): SpectatorStatus {
    return this._buildRenderData().status;
  }

  /** Returns true if the player is currently spectating. */
  get isSpectating(): boolean {
    return this._buildRenderData().isSpectating;
  }

  /** Returns true if player controls should be hidden. */
  get shouldHideControls(): boolean {
    return this._buildRenderData().hidePlayerControls;
  }

  /** Returns the sit-out gold amount. */
  get sitOutGold(): number {
    return this._state.sitOutNotification?.goldAwarded ?? 3;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the spectator mode UI and start listening for spectator events.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    this._notifyRenderCallbacks();
  }

  /**
   * Hide the spectator mode UI and stop listening for events.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset spectator state (called when returning to preparation phase).
   */
  reset(): void {
    this._state = {
      spectatorSelection: null,
      sitOutNotification: null,
      watchedCombatStarted: null,
      watchedCombatResult: null,
    };
    this._manuallyReset = true;
    this._previousPhase = '';
    this._notifyRenderCallbacks();
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: SpectatorModeRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to spectator selection events.
   * Returns an unsubscribe function.
   */
  onSpectatorSelected(callback: SpectatorSelectedCallback): () => void {
    this._selectedCallbacks.push(callback);
    return () => {
      const index = this._selectedCallbacks.indexOf(callback);
      if (index !== -1) {
        this._selectedCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to sit-out gold notification events.
   * Returns an unsubscribe function.
   */
  onSitOutGold(callback: SitOutGoldCallback): () => void {
    this._goldCallbacks.push(callback);
    return () => {
      const index = this._goldCallbacks.indexOf(callback);
      if (index !== -1) {
        this._goldCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Manual State Setting (for testing) ──────────────────────────────────

  /**
   * Manually set the spectator selection (for testing).
   */
  setSpectatorSelection(selection: SpectatorCombatSelected): void {
    this._state.spectatorSelection = selection;
    this._manuallyReset = false;
    this._notifySelectedCallbacks(selection);
    this._notifyRenderCallbacks();
  }

  /**
   * Manually set the sit-out notification (for testing).
   */
  setSitOutNotification(notification: SitOutNotification): void {
    this._state.sitOutNotification = notification;
    this._manuallyReset = false;
    this._notifyGoldCallbacks(notification);
    this._notifyRenderCallbacks();
  }

  /**
   * Manually set the watched combat started data (for testing).
   */
  setWatchedCombatStarted(combatStarted: CombatStarted): void {
    this._state.watchedCombatStarted = combatStarted;
    this._manuallyReset = false;
    this._notifyRenderCallbacks();
  }

  /**
   * Manually set the watched combat result (for testing).
   */
  setWatchedCombatResult(result: CombatResult): void {
    this._state.watchedCombatResult = result;
    this._manuallyReset = false;
    this._notifyRenderCallbacks();
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const state = event.data as { match: any };
        const matchState = state.match;

        // Reset when transitioning from combat to preparation, or when match ends
        const wasInCombat = this._previousPhase === 'combat';
        if (matchState.gameOver || (wasInCombat && matchState.phase === 'preparation')) {
          this.reset();
          this._previousPhase = matchState.phase;
          return; // reset() already notifies render callbacks
        }

        // Handle spectator combat selection from app shell state
        if (matchState.isSpectating && !this._state.spectatorSelection) {
          // Build selection from app shell state
          if (matchState.spectatorCombatPlayerAId && matchState.spectatorCombatPlayerBId) {
            this._handleSpectatorSelection({
              version: PROTOCOL_VERSION,
              messageType: 'SpectatorCombatSelected',
              matchId: matchState.matchId,
              spectatorPlayerId: this._playerId,
              combatPlayerAId: matchState.spectatorCombatPlayerAId,
              combatPlayerBId: matchState.spectatorCombatPlayerBId,
            });
          }
        }

        // Handle combat started for the watched combat
        if (matchState.combatStarted && this._state.spectatorSelection) {
          const combat = matchState.combatStarted;
          // Check if this is the watched combat
          if (this._isWatchedCombat(combat)) {
            this._state.watchedCombatStarted = combat;
          }
        }

        // Handle combat result for the watched combat
        if (matchState.lastCombatResult && this._state.watchedCombatStarted) {
          this._state.watchedCombatResult = matchState.lastCombatResult;
        }

        this._previousPhase = matchState.phase;
        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  /**
   * Check if a combat involves the watched combat pair.
   */
  private _isWatchedCombat(combat: CombatStarted): boolean {
    if (!this._state.spectatorSelection) return false;

    const playerA = this._state.spectatorSelection.combatPlayerAId;
    const playerB = this._state.spectatorSelection.combatPlayerBId;

    return (
      (combat.playerAId === playerA && combat.playerBId === playerB) ||
      (combat.playerAId === playerB && combat.playerBId === playerA)
    );
  }

  private _handleSpectatorSelection(selection: SpectatorCombatSelected): void {
    this._state.spectatorSelection = selection;
    this._manuallyReset = false;
    this._notifySelectedCallbacks(selection);
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifySelectedCallbacks(selection: SpectatorCombatSelected): void {
    for (const callback of this._selectedCallbacks) {
      callback(selection);
    }
  }

  private _notifyGoldCallbacks(notification: SitOutNotification): void {
    for (const callback of this._goldCallbacks) {
      callback(notification);
    }
  }

  private _buildRenderData(): SpectatorModeRenderData {
    const matchState = this._appShell.gameState.match;
    const isSpectatingFromShell = matchState.isSpectating;
    const selection = this._state.spectatorSelection;
    // The sit-out gold notification: the authoritative SitOutNotification
    // message (matchState — set by GameStateService when the server sends
    // it before the spectator's combat stream) is the primary source; the
    // internal copy (manual setter / tests) takes precedence when present.
    const notification = this._state.sitOutNotification ?? matchState.sitOutNotification ?? null;
    const combatStarted = this._state.watchedCombatStarted;
    const combatResult = this._state.watchedCombatResult;

    // Determine if spectating: either from the app shell's game state
    // or from internal state (for manual setters / testing).
    // Spectating is active during preparation (waiting) and combat (watching) phases.
    const isMatchActive = matchState.matchId !== null && !matchState.gameOver;
    // Only INTERNAL state counts here: the match-state notification is a
    // gold fallback source (see above), NOT a spectating signal — a stale
    // SitOutNotification in the match state (a reset without a fresh
    // RoundStarted) must not keep the UI "spectating" after the UI reset.
    const hasInternalState =
      this._state.spectatorSelection !== null || this._state.sitOutNotification !== null;
    // Internal state (manual setters) always counts. Shell state only counts if match is active
    // and not manually reset.
    const isSpectating = hasInternalState || (isMatchActive && isSpectatingFromShell && !this._manuallyReset);

    // Determine status
    let status: SpectatorStatus = 'idle';
    if (isSpectating) {
      if (combatResult) {
        status = 'finished';
      } else if (combatStarted) {
        status = 'watching';
      } else {
        status = 'waiting';
      }
    }

    // Determine if controls should be hidden
    const hidePlayerControls = isSpectating && status !== 'idle';

    // Build spectator message
    const spectatorMessage = this._buildSpectatorMessage(status, selection, notification, combatStarted, combatResult);

    return {
      isVisible: isSpectating || status !== 'idle',
      status,
      isSpectating,
      watchedPlayerAId: selection?.combatPlayerAId ?? matchState.spectatorCombatPlayerAId,
      watchedPlayerBId: selection?.combatPlayerBId ?? matchState.spectatorCombatPlayerBId,
      combatStarted: combatStarted !== null,
      combatEnded: combatResult !== null,
      sitOutGoldAwarded: notification?.goldAwarded ?? 3,
      sitOutGoldConfirmed: notification !== null,
      hidePlayerControls,
      spectatorMessage,
      spectatorSelection: selection,
      sitOutNotification: notification,
      watchedCombatStarted: combatStarted,
      watchedCombatResult: combatResult,
      matchId: matchState.matchId,
    };
  }

  private _buildSpectatorMessage(
    status: SpectatorStatus,
    selection: SpectatorCombatSelected | null,
    notification: SitOutNotification | null,
    combatStarted: CombatStarted | null,
    combatResult: CombatResult | null,
  ): string {
    const goldAmount = notification?.goldAwarded ?? 3;

    // Every non-idle state explicitly says "Spectating" so it is always
    // clear that the player is watching a fight between two OTHER players
    // (and not part of it).
    switch (status) {
      case 'idle':
        return '';
      case 'waiting':
        return selection
          ? `Spectating: ${selection.combatPlayerAId} vs ${selection.combatPlayerBId}. You receive ${goldAmount} gold for sitting out.`
          : `Spectating: sitting out this round. You will receive gold after combat.`;
      case 'watching':
        return `Spectating: ${selection?.combatPlayerAId ?? 'Player A'} vs ${selection?.combatPlayerBId ?? 'Player B'}. combat in progress.`;
      case 'finished':
        return `Spectating: combat complete. You received ${goldAmount} gold for sitting out.`;
      default:
        return '';
    }
  }
}
